import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { api, ApiError } from './api.js'
import { can } from './capabilities.js'

// <AuthProvider> calls /auth/me at boot behind a splash (Build Book §11a).
// httpOnly cookies mean the client has no other way to learn who it is or what
// it may do — /auth/me is not optional.

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [state, setState] = useState({ status: 'loading', me: null })

  const load = useCallback(async () => {
    try {
      const me = await api('/api/auth/me')
      setState({ status: 'authenticated', me })
      return me
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        setState({ status: 'anonymous', me: null })
        return null
      }
      // A network failure is NOT "signed out". Treating it as anonymous would
      // bounce a cashier to the login screen every time the wifi hiccups, and
      // they would lose the bill on screen.
      setState({ status: 'offline', me: null })
      return null
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const signIn = useCallback(
    async (email, password) => {
      const answer = await api('/api/auth/login', { method: 'POST', body: { email, password } })
      const me = await load()
      // `just_approved` is a fact about this sign-in, not about who signed in,
      // so it rides back from here rather than living on `me` (V013).
      return { me, justApproved: Boolean(answer?.just_approved) }
    },
    [load],
  )

  const signOut = useCallback(async () => {
    try {
      await api('/api/auth/logout', { method: 'POST' })
    } finally {
      setState({ status: 'anonymous', me: null })
    }
  }, [])

  const value = useMemo(
    () => ({ ...state, reload: load, signIn, signOut }),
    [state, load, signIn, signOut],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth() outside <AuthProvider>')
  return context
}

/**
 * Fails closed: unknown capability, missing array, or an unresolved /auth/me
 * all return false. It never falls back to inspecting the role.
 */
export function useCan() {
  const { me } = useAuth()
  return useCallback((capability) => can(me?.capabilities, capability), [me])
}

export function useTenant() {
  return useAuth().me?.tenant ?? null
}
